import { motion } from 'framer-motion';
import { SectionCard, Field, RadioSelect, inputClass, textareaClass } from '@/components/SurveyComponents';
import type { SurveyData } from '@/lib/index';

interface Props { data: SurveyData; update: (d: Partial<SurveyData>) => void; }

export function Step7Final({ data, update }: Props) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
      <div className="text-center mb-4">
        <h2 className="font-serif text-2xl font-bold text-foreground">Final Details 🌟</h2>
        <p className="text-muted-foreground text-sm mt-1">Almost there. These last questions help me make your itinerary flawless.</p>
      </div>
      <SectionCard title="Transportation" emoji="🚗">
        <Field label="How does your group prefer to get around?">
          <RadioSelect cols={1} options={['Private driver or car service for the whole trip','Rental car(s) — we drive ourselves','Mix of Uber / taxis / rideshare','Public transit when available','We figure it out as we go']} value={data.transportPrefs} onChange={v => update({ transportPrefs: v })} />
        </Field>
        <Field label="Do you need airport transfer coordination?">
          <RadioSelect cols={2} options={['Yes — for the whole group','Yes — for some of us','No — we have it handled','Not sure yet']} value={data.airportTransfer} onChange={v => update({ airportTransfer: v })} />
        </Field>
      </SectionCard>
      <SectionCard title="Group Dynamics" emoji="🫂">
        <Field label="Are there any group dynamics I should be aware of?" hint="e.g. someone has mobility issues, personality clashes, a surprise element, budget differences">
          <textarea className={textareaClass} placeholder="Anything that helps me plan a trip that works for everyone in the group..." value={data.groupDynamics} onChange={e => update({ groupDynamics: e.target.value })} />
        </Field>
        <Field label="Is anything about this trip a surprise?">
          <RadioSelect cols={2} options={['Yes — do not make it obvious in materials','No — everyone knows','Partially surprise']} value={data.isSurprise} onChange={v => update({ isSurprise: v })} />
        </Field>
      </SectionCard>
      <SectionCard title="Logistics & Notes" emoji="📋">
        <Field label="Do any travelers need special accommodations?">
          <textarea className={textareaClass} placeholder="Mobility accessibility, medical needs, traveling with a baby, service animal, etc." value={data.specialAccommodations} onChange={e => update({ specialAccommodations: e.target.value })} />
        </Field>
        <Field label="When do you need this itinerary by?">
          <input className={inputClass} placeholder="e.g. Within 2 weeks, by March 1st, ASAP..." value={data.itineraryDeadline} onChange={e => update({ itineraryDeadline: e.target.value })} />
        </Field>
        <Field label="How did you hear about Mapped by Mattie?" hint="If you haven't mentioned this already">
          <RadioSelect cols={2} options={['Instagram','TikTok','Pinterest','Google Search','Friend or family referral','I am a repeat client','Other']} value={data.referralSource} onChange={v => update({ referralSource: v })} />
        </Field>
        <Field label="Anything else you want me to know?" hint="Your dream trip in your own words, a non-negotiable, a fear, a secret wish — I am here for it all">
          <textarea className={textareaClass} rows={5} placeholder="Share anything and everything. The more context you give me, the better your itinerary will be..." value={data.finalNotes} onChange={e => update({ finalNotes: e.target.value })} />
        </Field>
      </SectionCard>
    </motion.div>
  );
}
